import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, SectionTitle } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Spinner } from '@/components/LoadingScreen';
import { ApiRequestError } from '@/services/api';
import {
  NOTIFICATION_KEYS,
  NOTIFICATION_LABELS,
  telegramService,
  type NotificationKey,
  type TelegramPreferences,
} from '@/services/telegram.service';
import { v } from '@/theme/tokens';

/**
 * Настройки → Telegram — what a connected person controls about themselves.
 *
 * Connecting is not here: a linking token is minted only by the owner, for a
 * named user, in Настройки → Пользователи и роли. What is left to the person
 * is their own notification switches and their own disconnect.
 */
export function TelegramSection() {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const status = useQuery({
    queryKey: ['telegram', 'status'],
    queryFn: ({ signal }) => telegramService.status(signal),
  });

  const fail = (fallback: string) => (e: unknown) =>
    setError(e instanceof ApiRequestError ? e.message : fallback);

  const refresh = () => void client.invalidateQueries({ queryKey: ['telegram'] });

  const disconnect = useMutation({
    mutationFn: telegramService.disconnect,
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail('Не удалось отключить Telegram'),
  });

  const setPreferences = useMutation({
    mutationFn: (preferences: TelegramPreferences) => telegramService.setPreferences(preferences),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail('Не удалось сохранить настройки уведомлений'),
  });

  const data = status.data;
  const preferences = (data?.notification_preferences ?? {}) as Record<string, boolean>;

  const toggle = (key: NotificationKey) => {
    const next = Object.fromEntries(
      NOTIFICATION_KEYS.map((k) => [k, k === key ? !(preferences[k] ?? true) : (preferences[k] ?? true)]),
    ) as TelegramPreferences;
    setPreferences.mutate(next);
  };

  return (
    <Card>
      <SectionTitle
        right={
          data ? (
            <Badge tone={data.connected ? 'positive' : 'neutral'}>
              {data.connected ? 'Подключён' : 'Не подключён'}
            </Badge>
          ) : undefined
        }
      >
        Telegram
      </SectionTitle>

      {status.isPending && <Spinner />}

      {data && (
        <>
          <p style={{ fontSize: 12.5, color: v('ink-muted'), marginTop: 0 }}>
            Бот отвечает на команды и присылает уведомления. Работает только на чтение: изменить
            данные через Telegram нельзя.
          </p>

          {!data.configured && (
            <div
              style={{
                background: v('warning-soft'),
                color: v('warning'),
                borderRadius: 10,
                padding: '9px 11px',
                fontSize: 12.5,
                marginBottom: 10,
              }}
            >
              Telegram-бот не настроен на сервере. Подключение станет доступно после настройки.
            </div>
          )}

          {error && (
            <div role="alert" style={{ color: v('negative'), fontSize: 12.5, marginBottom: 10 }}>
              {error}
            </div>
          )}

          {data.connected ? (
            <>
              <div style={{ fontSize: 13, marginBottom: 10 }}>
                {data.telegram_username ? `@${data.telegram_username}` : 'Аккаунт Telegram подключён'}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 11.5, color: v('ink-muted'), fontWeight: 600 }}>
                  Уведомления
                </div>
                {NOTIFICATION_KEYS.map((key) => (
                  <label
                    key={key}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
                  >
                    <input
                      type="checkbox"
                      aria-label={NOTIFICATION_LABELS[key]}
                      checked={preferences[key] ?? true}
                      onChange={() => toggle(key)}
                      disabled={setPreferences.isPending}
                    />
                    {NOTIFICATION_LABELS[key]}
                  </label>
                ))}
              </div>

              <Button variant="danger" onClick={() => disconnect.mutate()} disabled={disconnect.isPending}>
                {disconnect.isPending ? 'Отключение…' : 'Отключить Telegram'}
              </Button>
            </>
          ) : (
            <p style={{ fontSize: 13, color: v('ink-muted'), margin: 0 }}>
              Telegram не подключён. Подключение выполняет владелец: Настройки → Пользователи и
              роли. Он выпускает одноразовую ссылку персонально для вашей учётной записи.
            </p>
          )}
        </>
      )}
    </Card>
  );
}
